<template>
  <div class="upstox-auth">
    <!-- Not connected -->
    <div v-if="!isAuthenticated" class="auth-panel">
      <div class="auth-header">
        <div class="auth-icon">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <rect width="48" height="48" rx="12" fill="#5B2D8E"/>
            <path d="M14 32V20l10-8 10 8v12H14z" fill="white"/>
            <path d="M20 32v-8h8v8" stroke="#5B2D8E" stroke-width="2"/>
            <circle cx="24" cy="22" r="3" fill="#5B2D8E"/>
          </svg>
        </div>
        <div class="auth-title">
          <h3>Connect Upstox</h3>
          <p class="auth-subtitle">Access NSE, BSE, MCX & F&O data with real-time feeds</p>
        </div>
      </div>

      <div class="auth-features">
        <div class="feature-item">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"/>
          </svg>
          <span>Real-time market data via WebSocket</span>
        </div>
        <div class="feature-item">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"/>
          </svg>
          <span>NSE, BSE, MCX & F&O segments</span>
        </div>
        <div class="feature-item">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"/>
          </svg>
          <span>Historical candle data up to 10 years</span>
        </div>
      </div>

      <!-- Referral & Setup Guide -->
      <div class="broker-setup-guide">
        <div class="referral-section">
          <div class="referral-header">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
              <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"/>
            </svg>
            <span>Don't have an Upstox account?</span>
          </div>
          <a
            href="https://upstox.com/uplearn/subscription/?showTrial=true&utm_source=248280&utm_medium=&utm_campaign=ap_partner&referrerID=248280&referrerType=ap_partner"
            target="_blank"
            rel="noopener noreferrer"
            class="referral-link"
          >
            Open Upstox Account (Free)
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
              <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
            </svg>
          </a>
        </div>

        <button class="setup-guide-toggle" @click="showSetupGuide = !showSetupGuide">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
          </svg>
          {{ showSetupGuide ? 'Hide' : 'Show' }} API Setup Guide
        </button>

        <div v-if="showSetupGuide" class="setup-guide-content">
          <h4>How to get your Upstox API Key (Free)</h4>
          <ol class="setup-steps">
            <li>
              <strong>Create an Upstox Account</strong> — 
              <a href="https://upstox.com/uplearn/subscription/?showTrial=true&utm_source=248280&utm_medium=&utm_campaign=ap_partner&referrerID=248280&referrerType=ap_partner" target="_blank" rel="noopener noreferrer">Sign up here</a> 
              if you don't have one. Complete KYC verification.
            </li>
            <li>
              <strong>Go to Upstox Developer Console</strong> — 
              Visit <a href="https://account.upstox.com/developer/apps" target="_blank" rel="noopener noreferrer">Upstox Developer Apps</a> and log in with your Upstox credentials.
            </li>
            <li>
              <strong>Create a New App</strong> — Click "New App" and fill in:
              <ul class="setup-sub-steps">
                <li><strong>App Name:</strong> Any name (e.g., "TradingCandle")</li>
                <li><strong>Redirect URL:</strong> <code>https://tradingcandle.com/api/upstox/callback</code></li>
                <li><strong>Description:</strong> Market data charting platform</li>
              </ul>
            </li>
            <li>
              <strong>Get Your Credentials</strong> — After app creation, copy your:
              <ul class="setup-sub-steps">
                <li><strong>API Key</strong></li>
                <li><strong>API Secret</strong></li>
              </ul>
            </li>
            <li>
              <strong>Connect Below</strong> — Click "Connect with Upstox" and enter your API Key and Secret when prompted.
            </li>
          </ol>
          <p class="setup-note">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
            </svg>
            Upstox API access is free. No trading account balance required for market data.
          </p>
        </div>
      </div>

      <div class="auth-button-group">
        <button
          @click="loginWithUpstox"
          :disabled="isLoading"
          class="auth-button"
        >
          <span v-if="isLoading" class="btn-loading">
            <svg class="spinner" width="20" height="20" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="31.416" stroke-dashoffset="31.416">
                <animate attributeName="stroke-dashoffset" dur="0.6s" repeatCount="indefinite" from="31.416" to="0"/>
              </circle>
            </svg>
            Connecting to Upstox...
          </span>
          <span v-else>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 12l2 2 4-4"/>
              <circle cx="12" cy="12" r="10"/>
            </svg>
            Connect with Upstox
          </span>
        </button>
        
        <button 
          v-if="requiresUserCredentials && hasStoredCredentials" 
          @click="showChangeCredentialsModal" 
          class="change-credentials-button"
          :disabled="isLoading"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L14.293 8.5H.5a.5.5 0 0 1 0-1h13.793L2.146 2.146a.5.5 0 0 1 0-.708l-3-3z"/>
          </svg>
          Change Credentials
        </button>
      </div>

      <div v-if="error" class="error-message">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 16A8 8 0 108 0a8 8 0 000 16zM5.354 4.646a.5.5 0 10-.708.708L7.293 8l-2.647 2.646a.5.5 0 00.708.708L8 8.707l2.646 2.647a.5.5 0 00.708-.708L8.707 8l2.647-2.646a.5.5 0 00-.708-.708L8 7.293 5.354 4.646z"/>
        </svg>
        <span>{{ error }}</span>
      </div>

      <!-- Manual auth code entry -->
      <div v-if="showAuthCodeInput" class="auth-code-section">
        <div class="auth-code-header">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"/>
          </svg>
          <span>Manual Authentication Required</span>
        </div>
        <p class="auth-code-hint">
          After logging in, if you're not redirected automatically, copy the <code>code</code> from the URL and paste it below:
        </p>
        <div class="form-group">
          <label for="upstoxAuthCode">Authorization Code</label>
          <input
            id="upstoxAuthCode"
            v-model="authCode"
            type="text"
            placeholder="Paste code from URL"
            class="form-input"
          />
        </div>
        <div class="modal-actions">
          <button
            @click="completeWithCode"
            :disabled="!authCode || isLoading"
            class="auth-button primary"
          >
            <span v-if="isLoading" class="btn-loading">
              <svg class="spinner" width="16" height="16" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="31.416" stroke-dashoffset="31.416">
                  <animate attributeName="stroke-dashoffset" dur="0.6s" repeatCount="indefinite" from="31.416" to="0"/>
                </circle>
              </svg>
              Completing...
            </span>
            <span v-else>Complete Connection</span>
          </button>
          <button @click="showAuthCodeInput = false" class="cancel-button">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Connected -->
    <div v-else class="auth-success">
      <div class="success-header">
        <div class="success-icon">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <circle cx="32" cy="32" r="32" fill="#10B981"/>
            <path d="M20 32l8 8 16-16" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="success-title">
          <h3>Upstox Connected Successfully</h3>
          <p>Your account is linked and ready to trade</p>
        </div>
      </div>

      <div class="connection-status">
        <div class="status-item">
          <div class="status-dot online"></div>
          <span>Connected</span>
        </div>
        <div class="status-item">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 16s6-5.686 6-10A6 6 0 002 6c0 4.314 6 10 6 10zm0-7a3 3 0 100-6 3 3 0 000 6z"/>
          </svg>
          <span>Real-time data active</span>
        </div>
      </div>

      <div class="user-card" v-if="userProfile">
        <div class="user-avatar">
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <circle cx="20" cy="20" r="20" fill="#E5E7EB"/>
            <path d="M20 12C16.6863 12 14 14.6863 14 18C14 21.3137 16.6863 24 20 24C23.3137 24 26 21.3137 26 18C26 14.6863 23.3137 12 20 12Z" fill="#9CA3AF"/>
            <path d="M8 32C8 25.3726 13.3726 20 20 20C26.6274 20 32 25.3726 32 32" fill="#9CA3AF"/>
          </svg>
        </div>
        <div class="user-details">
          <h4>{{ userProfile.user_name || 'Upstox User' }}</h4>
          <div class="user-meta">
            <span class="user-id">ID: {{ userProfile.user_id || 'N/A' }}</span>
            <span class="user-email">{{ userProfile.email || 'N/A' }}</span>
          </div>
        </div>
      </div>

      <div class="auth-actions">
        <button
          @click="loginWithUpstox"
          class="auth-button reconnect-action"
          :disabled="isLoading"
        >
          Reconnect
        </button>
        <button
          v-if="requiresUserCredentials && hasStoredCredentials"
          @click="showChangeCredentialsModal"
          class="change-credentials-button"
          :disabled="isLoading"
        >
          Change Credentials
        </button>
        <button @click="disconnect" class="disconnect-button">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M5.5 3.5A.5.5 0 016 4v8a.5.5 0 01-.5.5h-2a.5.5 0 01-.5-.5V4a.5.5 0 01.5-.5h2z"/>
            <path d="M11.5 8a.5.5 0 01.5.5v3a.5.5 0 01-.5.5h-8a.5.5 0 01-.5-.5v-3a.5.5 0 01.5-.5h8z"/>
            <path d="M14 8a2 2 0 11-4 0 2 2 0 014 0z"/>
          </svg>
          Disconnect Account
        </button>
      </div>
    </div>

    <!-- Broker Credentials Modal -->
    <BrokerCredentialsModal
      :show="showCredentialsModal"
      :broker-type="'upstox'"
      :has-stored-credentials="hasStoredCredentials"
      :stored-api-key="storedApiKey"
      :start-in-edit="credentialModalStartsInEdit"
      @close="closeCredentialsModal"
      @save="saveCredentials"
      @use-stored="useStoredCredentials"
    />
  </div>
</template>

<script>
import UpstoxService from '../services/upstox_service.js';
import DataProviderManager from '../services/data_provider_manager.js';
import BrokerCredentialsModal from './BrokerCredentialsModal.vue';
import axios from 'axios';

const API_BASE = process.env.VUE_APP_API_BASE || '';

export default {
  name: 'UpstoxAuth',
  components: {
    BrokerCredentialsModal
  },
  props: {
    credentialSource: {
      type: String,
      default: null,
      validator: value => value === null || ['env', 'user'].includes(value)
    }
  },
  data() {
    return {
      isLoading: false,
      error: null,
      authCode: '',
      showAuthCodeInput: false,
      isAuthenticated: false,
      userProfile: null,
      showCredentialsModal: false,
      credentialModalStartsInEdit: false,
      credentialModalMode: 'manage',
      hasStoredCredentials: false,
      storedApiKey: '',
      requiresUserCredentials: this.credentialSource ? this.credentialSource === 'user' : true,
      showSetupGuide: false
    };
  },

  async mounted() {
    this.checkAuth();
    await this.checkStoredCredentials();
  },
  watch: {
    credentialSource(newSource) {
      if (newSource) {
        this.requiresUserCredentials = newSource === 'user';
      }
    }
  },

  methods: {
    checkAuth() {
      this.isAuthenticated = UpstoxService.isAuthenticated();
      if (this.isAuthenticated) {
        this.loadProfile();
      } else {
        UpstoxService.restoreSessionFromServer().then(restored => {
          if (restored) {
            this.isAuthenticated = true;
            this.loadProfile();
          }
        }).catch(() => {});
      }
    },

    async loadProfile() {
      try {
        const profileData = await UpstoxService.getProfile();
        if (profileData && profileData.success && profileData.data) {
          this.userProfile = profileData.data;
        }
      } catch (error) {
        console.warn('[Upstox] Could not load profile:', error.message);
      }
    },

    /**
     * Open Upstox login in a popup. After user logs in, Upstox redirects
     * to our server's /api/upstox/callback which stores the auth code.
     * In Tauri, uses in-app WebView; in web, uses popup.
     * We then call /api/upstox/complete to exchange the code for an access token.
     */
    async loginWithUpstox(skipCredentialRefresh = false) {
      this.isLoading = true;
      this.error = null;

      try {
        const credentialSource = await this.fetchCredentialSource();
        if (credentialSource) {
          this.requiresUserCredentials = credentialSource === 'user';
        }

        let credentialDetails = null;
        if (!skipCredentialRefresh) {
          credentialDetails = await this.checkStoredCredentials({ preserveOnError: true });
        }

        // Check if user has stored credentials first
        const hasCredentials = credentialDetails ? credentialDetails.hasCredentials : this.hasStoredCredentials;
        if (this.requiresUserCredentials && !hasCredentials) {
          this.credentialModalMode = 'connect';
          this.credentialModalStartsInEdit = false;
          this.showCredentialsModal = true;
          this.isLoading = false;
          return;
        }

        const authUrl = await UpstoxService.getAuthUrl();

        // Check if running in Tauri
        if (window.__TAURI__) {
          // Tauri environment - open OAuth in an in-app WebView window
          await this.openTauriOAuthWindow(authUrl);
          return;
        }

        // Web environment - open in popup
        const popup = window.open(authUrl, 'upstox_login', 'width=600,height=700');

        // Poll for the popup to close
        const pollInterval = setInterval(async () => {
          try {
            if (!popup || popup.closed) {
              clearInterval(pollInterval);

              await new Promise(resolve => setTimeout(resolve, 1500));

              try {
                const result = await UpstoxService.completeLogin();
                this.isAuthenticated = true;
                await this.loadProfile();
                DataProviderManager.switchProvider('upstox');
                this.$emit('authenticated', result);
              } catch (completeErr) {
                this.error = completeErr.response?.data?.error || completeErr.message;
                this.showAuthCodeInput = true;
              }
              this.isLoading = false;
            }
          } catch (e) {
            // Cross-origin access error is expected
          }
        }, 1000);

        // Safety timeout
        setTimeout(() => {
          clearInterval(pollInterval);
          if (this.isLoading) {
            this.isLoading = false;
            this.error = 'Login timed out. Please try again.';
          }
        }, 300000);

      } catch (error) {
        if (error.response?.data?.credentialSource === 'user') {
          this.requiresUserCredentials = true;
          this.hasStoredCredentials = false;
          this.credentialModalMode = 'connect';
          this.credentialModalStartsInEdit = false;
          this.showCredentialsModal = true;
          this.error = null;
          this.isLoading = false;
          return;
        }

        this.error = 'Failed to connect: ' + (error.response?.data?.error || error.message);
        this.isLoading = false;
      }
    },

    /**
     * Open OAuth in a Tauri WebviewWindow (in-app browser).
     */
    async openTauriOAuthWindow(authUrl) {
      try {
        const { WebviewWindow } = await import('@tauri-apps/api/webviewWindow');

        const oauthWindow = new WebviewWindow('upstox-oauth', {
          url: authUrl,
          title: 'Upstox Login',
          width: 600,
          height: 700,
          center: true,
          resizable: true,
          decorations: true
        });

        let isCompleted = false;
        let checkInterval = null;

        oauthWindow.once('tauri://destroyed', async () => {
          if (!isCompleted) {
            clearInterval(checkInterval);
            await this.completeOAuthFlow();
          }
        });

        checkInterval = setInterval(async () => {
          try {
            const status = await UpstoxService.checkAuthStatus();
            if (status.success) {
              isCompleted = true;
              clearInterval(checkInterval);
              oauthWindow.close();
              await this.completeOAuthFlow();
            }
          } catch (e) {
            // Ignore errors during polling
          }
        }, 2000);

        setTimeout(() => {
          clearInterval(checkInterval);
          if (!isCompleted && this.isLoading) {
            oauthWindow.close();
            this.isLoading = false;
            this.error = 'Login timed out. Please try again.';
          }
        }, 300000);

      } catch (error) {
        console.error('Failed to open Tauri OAuth window:', error);
        window.open(authUrl, '_blank');
        this.pollForTauriAuthCompletion();
      }
    },

    /**
     * Complete the OAuth flow after user authentication.
     */
    async completeOAuthFlow() {
      try {
        const result = await UpstoxService.completeLogin();
        this.isAuthenticated = true;
        await this.loadProfile();
        DataProviderManager.switchProvider('upstox');
        this.$emit('authenticated', result);
      } catch (completeErr) {
        this.error = completeErr.response?.data?.error || completeErr.message;
        this.showAuthCodeInput = true;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Fallback polling method for when using system browser in Tauri.
     */
    async pollForTauriAuthCompletion() {
      const maxAttempts = 150;
      let attempts = 0;

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const status = await UpstoxService.checkAuthStatus();

          if (status.success) {
            clearInterval(pollInterval);
            this.isAuthenticated = true;
            await this.loadProfile();
            DataProviderManager.switchProvider('upstox');
            this.$emit('authenticated', status);
            this.isLoading = false;
            return;
          }

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            this.isLoading = false;
            this.error = 'Login timed out. Please try again.';
          }
        } catch (error) {
          // Continue polling on error
        }
      }, 2000);
    },

    async completeWithCode() {
      this.isLoading = true;
      this.error = null;

      try {
        const result = await UpstoxService.completeLogin(this.authCode);
        if (result.success) {
          this.isAuthenticated = true;
          this.showAuthCodeInput = false;
          await this.loadProfile();
          DataProviderManager.switchProvider('upstox');
          this.$emit('authenticated', result);
        }
      } catch (error) {
        this.error = 'Authentication failed: ' + (error.response?.data?.error || error.message);
      } finally {
        this.isLoading = false;
      }
    },

    disconnect() {
      UpstoxService.disconnectFromServer();
      this.isAuthenticated = false;
      this.userProfile = null;
      DataProviderManager.switchProvider('binance');
      this.$emit('disconnected');
    },

    async checkStoredCredentials(options = {}) {
      try {
        const token = await this._getClerkToken();
        const response = await axios.get(API_BASE + '/api/broker-credentials/upstox/details', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        this.hasStoredCredentials = response.data.hasCredentials;
        this.storedApiKey = response.data.apiKey || '';
        this.requiresUserCredentials = response.data.requiresUserCredentials === true;
        return response.data;
      } catch (error) {
        console.warn('[Upstox] Could not check stored credentials:', error.message);
        if (!options.preserveOnError) {
          this.hasStoredCredentials = false;
          this.storedApiKey = '';
          this.requiresUserCredentials = this.credentialSource ? this.credentialSource === 'user' : true;
        }
        return null;
      }
    },

    async fetchCredentialSource() {
      try {
        const response = await axios.get((API_BASE || window.location.origin) + '/api/integrations/status');
        const setting = response.data?.integrations?.upstox;
        return setting && setting.credentialSource ? setting.credentialSource : null;
      } catch (error) {
        console.warn('[Upstox] Could not fetch credential source:', error.message);
        return null;
      }
    },

    async saveCredentials({ credentials }, controls) {
      try {
        const token = await this._getClerkToken();
        await axios.post(API_BASE + '/api/broker-credentials/save', {
          brokerType: 'upstox',
          credentials
        }, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        this.hasStoredCredentials = true;
        this.storedApiKey = credentials.apiKey;
        this.error = null;
        this.showCredentialsModal = false;
        this.credentialModalStartsInEdit = false;

        if (controls && controls.resolve) controls.resolve();

        this.$emit('credentials-saved', 'upstox');

        if (this.credentialModalMode === 'connect') {
          this.$nextTick(() => {
            this.loginWithUpstox(true);
          });
        }
      } catch (error) {
        const message = error.response?.data?.error || 'Failed to save credentials';
        if (controls && controls.reject) {
          controls.reject(new Error(message));
          return;
        }
        throw new Error(message);
      }
    },

    useStoredCredentials() {
      this.loginWithUpstox();
    },

    showChangeCredentialsModal() {
      this.credentialModalMode = 'manage';
      this.credentialModalStartsInEdit = this.hasStoredCredentials;
      this.showCredentialsModal = true;
    },

    closeCredentialsModal() {
      this.showCredentialsModal = false;
      this.credentialModalStartsInEdit = false;
      this.credentialModalMode = 'manage';
    },

    async _getClerkToken() {
      try {
        if (window.__vue_clerk && window.__vue_clerk.getToken) {
          return await window.__vue_clerk.getToken();
        }
        if (window.Clerk && window.Clerk.session) {
          return await window.Clerk.session.getToken();
        }
        if (window.Clerk && window.Clerk.getToken) {
          return await window.Clerk.getToken();
        }
      } catch (e) {
        console.warn('[Upstox] Could not get Clerk token:', e.message);
      }
      return null;
    }
  }
};
</script>

<style scoped>
/* Theme-aware via global CSS variables defined in App.vue (data-theme="dark"). */

.upstox-auth {
  max-width: 500px;
  margin: 0 auto;
  padding: 24px;
  color: var(--tc-text-primary);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.auth-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
}

.auth-icon {
  flex-shrink: 0;
}

.auth-title h3 {
  margin: 0 0 4px;
  font-size: 24px;
  font-weight: 700;
  color: var(--tc-text-primary);
  line-height: 1.2;
}

.auth-subtitle {
  margin: 0;
  font-size: 14px;
  color: var(--tc-text-secondary);
  line-height: 1.4;
}

.auth-features {
  display: grid;
  gap: 12px;
  margin-bottom: 24px;
  padding: 20px;
  background: var(--tc-bg-secondary);
  border-radius: 12px;
  border: 1px solid var(--tc-border-primary);
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: var(--tc-text-primary);
}

.feature-item svg {
  flex-shrink: 0;
  color: var(--tc-green);
}

.auth-button-group {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.auth-button {
  flex: 1;
  padding: 16px 24px;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.change-credentials-button {
  padding: 16px 20px;
  background: var(--tc-bg-secondary);
  color: var(--tc-text-primary);
  border: 1px solid var(--tc-border-primary);
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.change-credentials-button:hover {
  background: var(--tc-bg-hover);
  border-color: var(--tc-border-hover);
}

.auth-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(91, 45, 142, 0.1), 0 4px 6px -2px rgba(91, 45, 142, 0.05);
}

.auth-button:active:not(:disabled) {
  transform: translateY(0);
}

.auth-button:disabled {
  background: var(--tc-bg-tertiary);
  color: var(--tc-text-tertiary);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
  opacity: 0.6;
}

.auth-button.primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.1), 0 2px 4px -1px rgba(59, 130, 246, 0.06);
}

.btn-loading {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  margin-top: 16px;
  padding: 12px 16px;
  background: var(--tc-red-bg);
  color: var(--tc-red);
  border: 1px solid var(--tc-red);
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.auth-code-section {
  margin-top: 20px;
  padding: 20px;
  background: var(--tc-bg-secondary);
  border: 1px solid var(--tc-border-primary);
  border-radius: 12px;
}

.auth-code-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-weight: 600;
  color: var(--tc-text-primary);
}

.auth-code-hint {
  font-size: 14px;
  color: var(--tc-text-secondary);
  margin: 0 0 16px;
  line-height: 1.5;
}

.auth-code-hint code {
  background: var(--tc-bg-tertiary);
  color: var(--tc-text-primary);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 13px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: var(--tc-text-primary);
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  background: var(--tc-input-bg);
  color: var(--tc-input-text);
  border: 2px solid var(--tc-input-border);
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  transition: all 0.2s ease;
}

.form-input::placeholder {
  color: var(--tc-input-placeholder);
}

.form-input:focus {
  outline: none;
  border-color: var(--tc-accent);
  box-shadow: 0 0 0 3px var(--tc-accent-bg);
}

.modal-actions {
  display: flex;
  gap: 12px;
}

.modal-actions .auth-button {
  flex: 2;
  padding: 12px;
  font-size: 14px;
}

.cancel-button {
  flex: 1;
  padding: 12px;
  background: var(--tc-bg-secondary);
  color: var(--tc-text-secondary);
  border: 2px solid var(--tc-border-primary);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.cancel-button:hover {
  background: var(--tc-bg-hover);
  border-color: var(--tc-border-hover);
  color: var(--tc-text-primary);
}

.auth-success {
  text-align: center;
}

.success-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
}

.success-title h3 {
  margin: 16px 0 4px;
  font-size: 20px;
  font-weight: 700;
  color: var(--tc-green);
}

.success-title p {
  margin: 0;
  font-size: 14px;
  color: var(--tc-text-secondary);
}

.connection-status {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-bottom: 24px;
  padding: 16px;
  background: var(--tc-green-bg);
  border-radius: 12px;
  border: 1px solid var(--tc-green);
}

.status-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--tc-green);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
}

.status-dot.online::after {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border-radius: 50%;
  border: 2px solid #10b981;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.1); }
  100% { opacity: 1; transform: scale(1); }
}

.user-card {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding: 20px;
  background: var(--tc-card-bg);
  border-radius: 12px;
  border: 1px solid var(--tc-card-border);
  box-shadow: var(--tc-shadow-sm);
}

.user-details h4 {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--tc-text-primary);
}

.user-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-id, .user-email {
  font-size: 13px;
  color: var(--tc-text-secondary);
}

.auth-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.auth-actions .reconnect-action {
  flex: 0 1 auto;
  box-shadow: none;
}

.disconnect-button {
  padding: 12px 24px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 6px -1px rgba(239, 68, 68, 0.1);
}

.disconnect-button:hover {
  background: #dc2626;
  transform: translateY(-1px);
}

/* Dark/light theming handled by global CSS variables on [data-theme="dark"] in App.vue. */

/* Broker Setup Guide & Referral Styles */
.broker-setup-guide {
  margin-bottom: 20px;
  padding: 16px;
  background: var(--tc-bg-secondary);
  border: 1px solid var(--tc-border-primary);
  border-radius: 12px;
}

.referral-section {
  margin-bottom: 12px;
}

.referral-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--tc-text-secondary);
  margin-bottom: 8px;
}

.referral-header svg {
  flex-shrink: 0;
  color: var(--tc-accent);
}

.referral-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: var(--tc-accent-bg);
  color: var(--tc-accent);
  border: 1px solid var(--tc-accent);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
}

.referral-link:hover {
  background: var(--tc-accent);
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.setup-guide-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: none;
  border: 1px solid var(--tc-border-primary);
  border-radius: 8px;
  color: var(--tc-text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
}

.setup-guide-toggle:hover {
  background: var(--tc-bg-hover);
  color: var(--tc-text-primary);
  border-color: var(--tc-border-hover);
}

.setup-guide-toggle svg {
  flex-shrink: 0;
  color: var(--tc-accent);
}

.setup-guide-content {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--tc-border-primary);
}

.setup-guide-content h4 {
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 600;
  color: var(--tc-text-primary);
}

.setup-steps {
  margin: 0 0 16px;
  padding-left: 20px;
  list-style: decimal;
}

.setup-steps li {
  margin-bottom: 12px;
  font-size: 13px;
  line-height: 1.6;
  color: var(--tc-text-primary);
}

.setup-steps li a {
  color: var(--tc-accent);
  text-decoration: none;
  font-weight: 500;
}

.setup-steps li a:hover {
  text-decoration: underline;
}

.setup-sub-steps {
  margin: 8px 0 0;
  padding-left: 18px;
  list-style: disc;
}

.setup-sub-steps li {
  margin-bottom: 4px;
  font-size: 12px;
  color: var(--tc-text-secondary);
}

.setup-sub-steps li code {
  background: var(--tc-bg-tertiary);
  color: var(--tc-text-primary);
  padding: 1px 5px;
  border-radius: 3px;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 11px;
}

.setup-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 10px 12px;
  background: var(--tc-accent-bg);
  border-radius: 8px;
  font-size: 12px;
  color: var(--tc-accent);
  line-height: 1.5;
}

.setup-note svg {
  flex-shrink: 0;
  margin-top: 1px;
}
</style>
